/**
 * Типы угодий — пашня, пастбище, сенокос, залежь, многолетние насаждения,
 * несельхоз. Признак «Севооборот» означает, что на этом угодье ведётся полный
 * севооборот с паром (ADR-046: это только пашня).
 *
 * Тонкий wrapper над `ReferenceFieldCatalog` — см. FieldStates.tsx.
 */
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import {
  ReferenceFieldCatalog,
  type FieldCatalogRow,
} from '@/components/reference/ReferenceFieldCatalog';
import {
  useArchiveFieldCatalogItem,
  useCreateFieldCatalogItem,
  useFieldCatalogAdminList,
  useRestoreFieldCatalogItem,
  useUpdateFieldCatalogItem,
} from './hooks/useCatalogQueries';
import { toast } from '@/lib/toast';

export function fieldsPlural(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return 'полей';
  if (m10 === 1) return 'поле';
  if (m10 >= 2 && m10 <= 4) return 'поля';
  return 'полей';
}

export function PlatformAdminLandCategoriesPage() {
  const q = useFieldCatalogAdminList('land-categories');
  const createMut = useCreateFieldCatalogItem('land-categories');
  const updateMut = useUpdateFieldCatalogItem('land-categories');
  const archiveMut = useArchiveFieldCatalogItem('land-categories');
  const restoreMut = useRestoreFieldCatalogItem('land-categories');

  const items: FieldCatalogRow[] = q.data?.items ?? [];
  const active = items.filter((i) => i.is_active).length;

  return (
    <PlatformAdminLayout>
      <ReferenceFieldCatalog
        title="Типы угодий"
        subtitle={`Справочник · ${active} в работе, ${items.length - active} в архиве`}
        createLabel="Добавить тип угодья"
        usageUnit={fieldsPlural}
        withRotation
        items={items}
        isLoading={q.isLoading}
        onCreate={async (payload) => {
          try {
            await createMut.mutateAsync(payload);
            toast.success('Тип угодья добавлен');
          } catch (err) {
            toast.error((err as Error).message);
            throw err;
          }
        }}
        onUpdate={async (id, payload) => {
          try {
            await updateMut.mutateAsync({ id, payload });
            toast.success('Тип угодья сохранён');
          } catch (err) {
            toast.error((err as Error).message);
            throw err;
          }
        }}
        onArchive={async (id) => {
          try {
            await archiveMut.mutateAsync(id);
            toast.success('Тип угодья убран в архив');
          } catch (err) {
            toast.error((err as Error).message);
            throw err;
          }
        }}
        onRestore={async (id) => {
          try {
            await restoreMut.mutateAsync(id);
            toast.success('Тип угодья возвращён из архива');
          } catch (err) {
            toast.error((err as Error).message);
            throw err;
          }
        }}
      />
      <p className="mt-3 text-[11px] text-muted-foreground">
        «Севооборот» включают только тем угодьям, где поле проходит полный круг с
        паром. У остальных — упрощённый уход без севооборота.
      </p>
    </PlatformAdminLayout>
  );
}

export default PlatformAdminLandCategoriesPage;
